import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert } from '../Alert';
import { Box } from '../Box';
import { Text } from '../Text';

export interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// Top-level app boundary — full-viewport fallback, sin retry: un fallo a esta
// altura no es recuperable sin recargar. Para aislar una sección con
// "Reintentar", usar FeatureErrorBoundary.
export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ui] errorBoundary', {
      name: error.name,
      message: error.message,
      componentStack: info.componentStack,
    });
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Box sx={{ maxWidth: 480 }}>
            <Alert severity="error">
              {/* TODO: i18n */}
              <Text variant="h6">Algo ha fallado</Text>
              <Text variant="body2">{this.state.error?.message ?? 'Error desconocido'}</Text>
            </Alert>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}
